import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { Clock, CloudArrowDown, Airplane, ArrowsClockwise } from "@phosphor-icons/react";
import { useAuth } from "../contexts/AuthContext";

const fmt = (val) => val !== null && val !== undefined ? val.toLocaleString("en-NZ") : "—";
const fmtDate = (iso) => {
  if (!iso) return "Never";
  return new Date(iso).toLocaleString("en-NZ", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
};
const fmtCountdown = (isoNextRun) => {
  if (!isoNextRun) return "—";
  const diff = new Date(isoNextRun) - Date.now();
  if (diff <= 0) return "Imminent";
  const h = Math.floor(diff / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

const StatusDot = ({ status }) => {
  const colors = {
    connected: "bg-emerald-400", active: "bg-emerald-400",
    reachable: "bg-amber-400",   error: "bg-red-400", unknown: "bg-gray-500",
  };
  return <span className={`inline-block w-2 h-2 rounded-full ${colors[status] ?? colors.unknown} mr-2 mt-0.5 flex-shrink-0`} />;
};

function SourceCard({ title, icon: Icon, status, children, actionSlot }) {
  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
      className="bg-[#0F2044] rounded-xl border border-[rgba(255,255,255,0.08)] p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-[rgba(255,255,255,0.06)] flex items-center justify-center">
            <Icon size={20} weight="duotone" className="text-[#8EAADF]" />
          </div>
          <div>
            <h3 className="font-syne font-bold text-white text-sm">{title}</h3>
            <div className="flex items-center mt-0.5">
              <StatusDot status={status} />
              <span className="text-xs text-[#8EAADF] capitalize">{status}</span>
            </div>
          </div>
        </div>
        {actionSlot}
      </div>
      {children}
    </motion.div>
  );
}

function StatRow({ label, value, mono = true }) {
  return (
    <div className="flex justify-between items-center py-1.5 border-b border-[rgba(255,255,255,0.05)] last:border-0">
      <span className="text-xs text-[#8EAADF]">{label}</span>
      <span className={`text-xs text-white ${mono ? "font-mono" : ""}`}>{value}</span>
    </div>
  );
}

export default function DataSources() {
  const { api, user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [serpapi, setSerpapi]             = useState(null);
  const [airlineDirect, setAirlineDirect] = useState(null);
  const [scheduler, setScheduler]         = useState(null);
  const [schedHistory, setSchedHistory]   = useState([]);
  const [freshness, setFreshness]         = useState(null);
  const [fetchJobs, setFetchJobs]         = useState([]);
  const [duffel, setDuffel]               = useState(null);
  const [kiwi, setKiwi]                   = useState(null);
  const [brightdata, setBrightdata]       = useState(null);
  const [zyte, setZyte]                   = useState(null);
  const [quota, setQuota]                 = useState(null);
  const [testing, setTesting]             = useState({ serpapi: false, airline: false, duffel: false, kiwi: false, brightdata: false, zyte: false });
  const [testResult, setTestResult]       = useState({ serpapi: null, airline: null, duffel: null, kiwi: null, brightdata: null, zyte: null });
  const [runningNow, setRunningNow]       = useState(false);
  const [programme, setProgramme]         = useState("CI");

  const load = useCallback(async () => {
    const [sRes, aRes, schRes, schHistRes, freshRes, jobsRes, duffelRes, kiwiRes, bdRes, zyteRes] = await Promise.allSettled([
      api.get("/settings/serpapi"),
      api.get("/settings/airline-direct"),
      api.get("/scheduler/status"),
      api.get("/scheduler/history", { params: { programme } }),
      api.get("/data-sources/freshness", { params: { programme } }),
      api.get("/fetch-jobs", { params: { limit: 20, programme } }),
      api.get("/settings/duffel"),
      api.get("/settings/kiwi"),
      api.get("/settings/brightdata"),
      api.get("/settings/zyte"),
    ]);
    if (sRes.status === "fulfilled")       setSerpapi(sRes.value.data);
    if (aRes.status === "fulfilled")       setAirlineDirect(aRes.value.data);
    if (schRes.status === "fulfilled")     setScheduler(schRes.value.data);
    if (schHistRes.status === "fulfilled") setSchedHistory(schHistRes.value.data || []);
    if (freshRes.status === "fulfilled")   setFreshness(freshRes.value.data);
    if (jobsRes.status === "fulfilled")    setFetchJobs(jobsRes.value.data || []);
    if (duffelRes.status === "fulfilled")  setDuffel(duffelRes.value.data);
    if (kiwiRes.status === "fulfilled")    setKiwi(kiwiRes.value.data);
    if (bdRes.status === "fulfilled")      setBrightdata(bdRes.value.data);
    if (zyteRes.status === "fulfilled")    setZyte(zyteRes.value.data);
    try { const q = await api.get("/serpapi/quota", { params: { programme } }); setQuota(q.data); } catch { /* ignore */ }
  }, [api, programme]);

  useEffect(() => { load(); }, [load]);

  const testSerpapi = async () => {
    setTesting(t => ({ ...t, serpapi: true }));
    try {
      const { data } = await api.post("/settings/serpapi/test");
      setTestResult(t => ({ ...t, serpapi: data }));
    } catch { setTestResult(t => ({ ...t, serpapi: { status: "error", message: "Request failed" } })); }
    finally { setTesting(t => ({ ...t, serpapi: false })); }
  };

  const testAirlineDirect = async () => {
    setTesting(t => ({ ...t, airline: true }));
    try {
      const { data } = await api.post("/settings/airline-direct/test");
      setTestResult(t => ({ ...t, airline: data }));
    } catch { setTestResult(t => ({ ...t, airline: { status: "error", message: "Request failed" } })); }
    finally { setTesting(t => ({ ...t, airline: false })); }
  };

  const testDuffel = async () => {
    setTesting(t => ({ ...t, duffel: true }));
    try {
      const { data } = await api.post("/settings/duffel/test");
      setTestResult(t => ({ ...t, duffel: data }));
    } catch { setTestResult(t => ({ ...t, duffel: { status: "error", message: "Request failed" } })); }
    finally { setTesting(t => ({ ...t, duffel: false })); }
  };

  const testKiwi = async () => {
    setTesting(t => ({ ...t, kiwi: true }));
    try {
      const { data } = await api.post("/settings/kiwi/test");
      setTestResult(t => ({ ...t, kiwi: data }));
    } catch { setTestResult(t => ({ ...t, kiwi: { status: "error", message: "Request failed" } })); }
    finally { setTesting(t => ({ ...t, kiwi: false })); }
  };

  const testBrightdata = async () => {
    setTesting(t => ({ ...t, brightdata: true }));
    try {
      const { data } = await api.post("/settings/brightdata/test");
      setTestResult(t => ({ ...t, brightdata: data }));
    } catch { setTestResult(t => ({ ...t, brightdata: { status: "error", message: "Request failed" } })); }
    finally { setTesting(t => ({ ...t, brightdata: false })); }
  };

  const testZyte = async () => {
    setTesting(t => ({ ...t, zyte: true }));
    try {
      const { data } = await api.post("/settings/zyte/test");
      setTestResult(t => ({ ...t, zyte: data }));
    } catch { setTestResult(t => ({ ...t, zyte: { status: "error", message: "Request failed" } })); }
    finally { setTesting(t => ({ ...t, zyte: false })); }
  };

  const runSchedulerNow = async () => {
    setRunningNow(true);
    try { await api.post("/scheduler/run-now"); setTimeout(load, 2000); }
    catch (err) { console.error(err); }
    finally { setTimeout(() => setRunningNow(false), 3000); }
  };

  const FreshnessGrid = () => {
    if (!freshness) return <div className="text-[#8EAADF] text-sm">Loading…</div>;
    const ageColor = (h) => {
      if (h === null) return "bg-[rgba(255,255,255,0.05)] text-[#8EAADF]";
      if (h < 26)    return "bg-emerald-900/40 text-emerald-400";
      if (h < 50)    return "bg-amber-900/40 text-amber-400";
      return "bg-red-900/40 text-red-400";
    };
    return (
      <div className="overflow-x-auto">
        <table className="w-full text-xs font-mono">
          <thead>
            <tr>
              <th className="text-left text-[#8EAADF] pb-2 pr-4 font-normal">Route</th>
              {freshness.months?.map(m => (
                <th key={m.label} className="text-[#8EAADF] pb-2 px-1 font-normal text-center">{m.short}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {freshness.routes?.map(route => (
              <tr key={route.route_id}>
                <td className="pr-4 py-0.5 text-white">{route.label}</td>
                {route.months?.map((cell, i) => (
                  <td key={i} className="px-1 py-0.5 text-center">
                    <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] ${ageColor(cell.age_hours)}`}
                      title={cell.fetched_at ? fmtDate(cell.fetched_at) : "Not fetched"}>
                      {cell.age_hours !== null ? `${Math.round(cell.age_hours)}h` : "—"}
                    </span>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div data-testid="data-sources-page" className="p-6 max-w-screen-xl mx-auto space-y-8">

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="font-syne font-bold text-2xl text-white tracking-tight">Data Sources</h1>
          <p className="text-[#8EAADF] text-sm mt-1">Monitor fare data pipelines, scheduler health, and source freshness</p>
        </div>
        <div className="flex bg-card-bg rounded-lg border border-[rgba(255,255,255,0.08)] p-0.5" data-testid="data-sources-programme-filter">
          {[["CI", "China Airlines"], ["PX", "Air Niugini"]].map(([c, l]) => (
            <button key={c} data-testid={`data-sources-programme-${c.toLowerCase()}`} onClick={() => setProgramme(c)}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${programme === c ? "bg-ci-blue text-white" : "text-text-secondary hover:text-white"}`}>{l}</button>
          ))}
        </div>
      </div>

      {/* New tier priority — Improvement 3 */}
      <section data-testid="tier-priority-section" className="bg-gradient-to-r from-[#0F2044] to-[#0A1428] border border-ci-blue/30 rounded-xl p-6">
        <h2 className="font-syne font-bold text-white text-base mb-1">Tier Priority (updated Feb 2026)</h2>
        <p className="text-[#8EAADF] text-xs mb-4">Tiers run in this order to fill gaps. When more than one source has a price for the same cell, the matrix shows the most trusted source first — Google Flights (the team's manual benchmark), then Duffel/Kiwi, then Trip.com, then keyed GDS fares — not simply the lowest number.</p>
        <ol className="space-y-2 text-sm">
          <li className="flex items-start gap-3">
            <span className="font-mono text-xs bg-ci-orange/20 text-ci-orange border border-ci-orange/40 rounded px-2 py-0.5">Tier 1</span>
            <div>
              <span className="text-white font-semibold">Airline Direct</span>
              <span className="text-text-secondary"> — fetched from each airline's own booking site. Live for <span className="font-mono text-ci-orange">CI</span>, but only as the china-airlines.com NZ <em>"From NZD"</em> promo widget — a reference price that is <strong className="text-white">not date-specific</strong>, so it feeds the CI Promo tab and never replaces a Google Flights / Trip.com fare in the matrix. Per-airline date-specific parsers planned for CI, SQ, MH, QF, CX, NZ.</span>
            </div>
          </li>
          <li className="flex items-start gap-3">
            <span className="font-mono text-xs bg-blue-500/20 text-blue-300 border border-blue-500/40 rounded px-2 py-0.5">Tier 2</span>
            <div>
              <span className="text-white font-semibold">SerpAPI (Google Flights)</span>
              <span className="text-text-secondary"> — fills airlines not covered by Tier 1. Includes ±2 day fallback for cells with no exact-date match.</span>
            </div>
          </li>
          <li className="flex items-start gap-3">
            <span className="font-mono text-xs bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 rounded px-2 py-0.5">Tier 3</span>
            <div>
              <span className="text-white font-semibold">Zyte AI Browser + Bright Data (parallel)</span>
              <span className="text-text-secondary"> — Trip.com aggregator scrape, returns prices for every airline on the route in one call. ±2 day fallback included.</span>
            </div>
          </li>
          <li className="flex items-start gap-3">
            <span className="font-mono text-xs bg-purple-500/20 text-purple-300 border border-purple-500/40 rounded px-2 py-0.5">Tier 4</span>
            <div>
              <span className="text-white font-semibold">Duffel + Kiwi.com Tequila</span>
              <span className="text-text-secondary"> — last-resort fallbacks for airlines that none of the above tiers could price.</span>
            </div>
          </li>
        </ol>
        <p className="text-[10px] text-text-secondary mt-4 italic">
          When a fare has the orange border in the matrix, it came from Tier 1 (airline direct). Other border colours indicate fallback tier — see legend.
        </p>
      </section>

      <section>
        <h2 className="font-syne font-bold text-white text-base mb-3">Scheduler</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-[#0F2044] rounded-xl border border-[rgba(255,255,255,0.08)] p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Clock size={18} weight="duotone" className="text-[#8EAADF]" />
                <span className="font-syne font-bold text-white text-sm">Daily Fare Matrix</span>
              </div>
              {isAdmin && (
                <button onClick={runSchedulerNow} disabled={runningNow} data-testid="run-now-btn"
                  className="text-xs bg-[#E8501A] hover:bg-[#d44416] text-white px-3 py-1.5 rounded-lg font-medium transition-colors disabled:opacity-50">
                  {runningNow ? "Running…" : "Run Now"}
                </button>
              )}
            </div>
            <StatRow label="Schedule"    value="Daily 13:00 UTC (2:00 AM NZT)" mono={false} />
            <StatRow label="Sources"     value="Airline Direct → SerpAPI → Zyte/Bright Data → Duffel/Kiwi" mono={false} />
            <StatRow label="Next run"    value={fmtCountdown(scheduler?.daily_matrix?.next_run)} />
            <StatRow label="Last run"    value={fmtDate(scheduler?.daily_matrix?.last_run?.completed_at)} mono={false} />
            <StatRow label="Last status" value={scheduler?.daily_matrix?.last_run?.status ?? "—"} mono={false} />
          </div>
          <div className="bg-[#0F2044] rounded-xl border border-[rgba(255,255,255,0.08)] p-5">
            <div className="flex items-center gap-2 mb-3">
              <Airplane size={18} weight="duotone" className="text-[#8EAADF]" />
              <span className="font-syne font-bold text-white text-sm">Weekly OW Tracker (BNE)</span>
            </div>
            <StatRow label="Schedule" value="Every Monday 14:00 UTC" mono={false} />
            <StatRow label="Route"    value="AKL → BNE one-way"      mono={false} />
            <StatRow label="Airlines" value="CI vs NZ"               mono={false} />
            <StatRow label="Next run" value={fmtCountdown(scheduler?.weekly_ow_tracker?.next_run)} />
            <StatRow label="Last run" value={fmtDate(scheduler?.weekly_ow_tracker?.last_run?.completed_at)} mono={false} />
          </div>
        </div>
      </section>

      <section>
        <h2 className="font-syne font-bold text-white text-base mb-3">Live Data Sources</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

          <SourceCard title="Google Flights via SerpAPI" icon={CloudArrowDown}
            status={serpapi?.configured ? "connected" : "error"}
            actionSlot={isAdmin && (
              <button onClick={testSerpapi} disabled={testing.serpapi} data-testid="test-serpapi-btn"
                className="text-xs border border-[rgba(255,255,255,0.15)] text-[#8EAADF] hover:text-white px-3 py-1.5 rounded-lg transition-colors disabled:opacity-50">
                {testing.serpapi ? "Testing…" : "Test"}
              </button>
            )}>
            <StatRow label="API key"         value={serpapi?.key_preview ?? "Not set"} />
            <StatRow label="Plan"            value={quota?.plan_name ?? "—"} mono={false} />
            <StatRow label="Balance"         value={quota ? `${quota.total_searches_left?.toLocaleString() ?? "—"} / ${quota.searches_per_month?.toLocaleString() ?? "—"} searches` : "loading…"} mono={false} />
            <StatRow label="This month used" value={fmt(quota?.this_month_usage)} />
            <StatRow label="Last fetch"      value={fmtDate(serpapi?.last_fetch)} mono={false} />
            <StatRow label="Routes covered"  value="All 11 routes" mono={false} />
            {quota?.low_balance && (
              <div className="mt-3 p-3 rounded-lg text-xs bg-red-900/30 text-red-300 border border-red-800" data-testid="ds-quota-warning">
                ⚠ Balance low ({quota.total_searches_left} &lt; {quota.low_balance_threshold}). Top up at serpapi.com
              </div>
            )}
            {testResult.serpapi && (
              <div className={`mt-3 p-3 rounded-lg text-xs font-mono ${testResult.serpapi.status === "connected" ? "bg-emerald-900/30 text-emerald-400" : "bg-red-900/30 text-red-400"}`}>
                {testResult.serpapi.status === "connected"
                  ? `✓ Connected · ${testResult.serpapi.latency_ms}ms · ${testResult.serpapi.credits_remaining ?? "?"} credits left`
                  : `✗ ${testResult.serpapi.message}`}
              </div>
            )}
          </SourceCard>

          <SourceCard title="China Airlines Direct (chinaairlines.com)" icon={ArrowsClockwise}
            status={airlineDirect?.status ?? "unknown"}
            actionSlot={isAdmin && (
              <button onClick={testAirlineDirect} disabled={testing.airline} data-testid="test-airline-direct-btn"
                className="text-xs border border-[rgba(255,255,255,0.15)] text-[#8EAADF] hover:text-white px-3 py-1.5 rounded-lg transition-colors disabled:opacity-50">
                {testing.airline ? "Testing…" : "Test"}
              </button>
            )}>
            <StatRow label="No API key required" value="✓ Free"              mono={false} />
            <StatRow label="Airlines covered"    value="CI (China Airlines)" mono={false} />
            <StatRow label="Monthly fetches"     value={fmt(airlineDirect?.monthly_fetches)} />
            <StatRow label="CI fares stored"     value={fmt(airlineDirect?.ci_fares_stored)} />
            <StatRow label="Last fetch"          value={fmtDate(airlineDirect?.last_fetch)} mono={false} />
            {testResult.airline && (
              <div className={`mt-3 p-3 rounded-lg text-xs font-mono ${
                testResult.airline.status === "connected" ? "bg-emerald-900/30 text-emerald-400"
                : testResult.airline.status === "reachable" ? "bg-amber-900/30 text-amber-400"
                : "bg-red-900/30 text-red-400"}`}>
                {testResult.airline.status === "connected"
                  ? `✓ Connected · ${testResult.airline.latency_ms}ms · Sample fare: NZD ${testResult.airline.sample_fare?.toLocaleString()}`
                  : testResult.airline.status === "reachable"
                  ? `⚠ Reachable · ${testResult.airline.latency_ms}ms · ${testResult.airline.message}`
                  : `✗ ${testResult.airline.message}`}
              </div>
            )}
            <div className="mt-3 p-3 bg-[rgba(255,255,255,0.03)] rounded-lg">
              <p className="text-xs text-[#8EAADF]">
                Fetches CI's own published fares directly — captures promotions and fares not always visible on aggregators like Google Flights.
              </p>
            </div>
          </SourceCard>

          <SourceCard
            title="Duffel — 300+ Airlines"
            icon={CloudArrowDown}
            status={!duffel ? "unknown" : duffel.configured ? "connected" : "error"}
            actionSlot={isAdmin && (
              <button onClick={testDuffel} disabled={testing.duffel || !duffel?.configured} data-testid="test-duffel-btn"
                className="text-xs border border-[rgba(255,255,255,0.15)] text-[#8EAADF] hover:text-white px-3 py-1.5 rounded-lg transition-colors disabled:opacity-50">
                {testing.duffel ? "Testing…" : "Test"}
              </button>
            )}>
            <StatRow label="API key"         value={duffel?.key_preview ?? "Not set"} />
            <StatRow label="Mode"            value={duffel?.mode ?? "—"} mono={false} />
            <StatRow label="Monthly fetches" value={fmt(duffel?.monthly_fetches)} />
            <StatRow label="Fares stored"    value={fmt(duffel?.fares_stored)} />
            <StatRow label="Last fetch"      value={fmtDate(duffel?.last_fetch)} mono={false} />
            <StatRow label="Coverage"        value="SQ, CX, MH, NZ, QF, KE, PR, FJ + all others" mono={false} />
            {testResult.duffel && (
              <div className={`mt-3 p-3 rounded-lg text-xs font-mono ${
                testResult.duffel.status === "connected"
                  ? "bg-emerald-900/30 text-emerald-400"
                  : "bg-red-900/30 text-red-400"}`}>
                {testResult.duffel.status === "connected"
                  ? `✓ Connected · ${testResult.duffel.latency_ms}ms · ${testResult.duffel.fares_found} fares found on test route`
                  : `✗ ${testResult.duffel.message}`}
              </div>
            )}
            <div className="mt-3 p-3 bg-[rgba(255,255,255,0.03)] rounded-lg">
              <p className="text-xs text-[#8EAADF]">
                Automatic fallback — fires for any airline SerpAPI misses.
                Covers all 300+ airlines including SQ, MU, FJ that rarely appear on Google Flights.
              </p>
            </div>
          </SourceCard>

          <SourceCard
            title="Kiwi.com Tequila — Virtual Interlining"
            icon={CloudArrowDown}
            status={!kiwi ? "unknown" : kiwi.configured ? "connected" : "error"}
            actionSlot={isAdmin && (
              <button onClick={testKiwi} disabled={testing.kiwi || !kiwi?.configured} data-testid="test-kiwi-btn"
                className="text-xs border border-[rgba(255,255,255,0.15)] text-[#8EAADF] hover:text-white px-3 py-1.5 rounded-lg transition-colors disabled:opacity-50">
                {testing.kiwi ? "Testing…" : "Test"}
              </button>
            )}>
            <StatRow label="API key"         value={kiwi?.key_preview ?? "Not set"} />
            <StatRow label="Tier"            value={kiwi?.tier ?? "Tier 3 fallback"} mono={false} />
            <StatRow label="Monthly fetches" value={fmt(kiwi?.monthly_fetches)} />
            <StatRow label="Fares stored"    value={fmt(kiwi?.fares_stored)} />
            <StatRow label="Last fetch"      value={fmtDate(kiwi?.last_fetch)} mono={false} />
            <StatRow label="Coverage"        value="All carriers + virtual interlining" mono={false} />
            {testResult.kiwi && (
              <div className={`mt-3 p-3 rounded-lg text-xs font-mono ${
                testResult.kiwi.status === "connected"
                  ? "bg-emerald-900/30 text-emerald-400"
                  : "bg-red-900/30 text-red-400"}`}>
                {testResult.kiwi.status === "connected"
                  ? `✓ Connected · ${testResult.kiwi.latency_ms}ms · ${testResult.kiwi.fares_found} fares found on test route`
                  : `✗ ${testResult.kiwi.message}`}
              </div>
            )}
            <div className="mt-3 p-3 bg-[rgba(255,255,255,0.03)] rounded-lg">
              <p className="text-xs text-[#8EAADF]">
                {kiwi?.configured
                  ? "Tier 3 fallback — only fires for airlines missed by both SerpAPI and Duffel. Uses Kiwi's virtual interlining to surface low fares that single-carrier APIs can't combine."
                  : "Not configured — add KIWI_API_KEY to backend/.env to activate. Obtain at tequila.kiwi.com/portal."}
              </p>
            </div>
          </SourceCard>

          <SourceCard
            title="Bright Data — Direct Airline Websites"
            icon={CloudArrowDown}
            status={!brightdata ? "unknown" : brightdata.configured ? "connected" : "error"}
            actionSlot={isAdmin && (
              <button onClick={testBrightdata} disabled={testing.brightdata || !brightdata?.configured} data-testid="test-brightdata-btn"
                className="text-xs border border-[rgba(255,255,255,0.15)] text-[#8EAADF] hover:text-white px-3 py-1.5 rounded-lg transition-colors disabled:opacity-50">
                {testing.brightdata ? "Testing…" : "Test"}
              </button>
            )}>
            <StatRow label="API key"         value={brightdata?.key_preview ?? "Not set"} />
            <StatRow label="Tier"            value="4 — Final fallback (direct airline sites)" mono={false} />
            <StatRow label="Monthly fetches" value={fmt(brightdata?.monthly_fetches)} />
            <StatRow label="Fares stored"    value={fmt(brightdata?.fares_stored)} />
            <StatRow label="Last fetch"      value={fmtDate(brightdata?.last_fetch)} mono={false} />
            <StatRow label="Airlines"        value="CX, MH, QF, NZ, SQ, CI, KE, JQ, PR, FJ, CZ, MU" mono={false} />
            {testResult.brightdata && (
              <div className={`mt-3 p-3 rounded-lg text-xs font-mono ${
                testResult.brightdata.status === "connected" ? "bg-emerald-900/30 text-emerald-400"
                : testResult.brightdata.status === "reachable" ? "bg-amber-900/30 text-amber-400"
                : "bg-red-900/30 text-red-400"}`}>
                {testResult.brightdata.status === "connected"
                  ? `✓ Connected · ${testResult.brightdata.latency_ms}ms · Live fare: NZD ${testResult.brightdata.sample_fare?.toLocaleString()}`
                  : testResult.brightdata.status === "reachable"
                  ? `⚠ Connected · ${testResult.brightdata.latency_ms}ms · ${testResult.brightdata.message}`
                  : `✗ ${testResult.brightdata.message}`}
              </div>
            )}
            <div className="mt-3 p-3 bg-[rgba(255,255,255,0.03)] rounded-lg">
              <p className="text-xs text-[#8EAADF]">
                Tier 4 fallback — only fires when SerpAPI, Duffel and Kiwi all return no data.
                Uses Bright Data Web Unlocker to render real airline booking pages and extract live fares.
              </p>
            </div>
          </SourceCard>

          <SourceCard
            title="Zyte — AI Browser Rendering"
            icon={CloudArrowDown}
            status={!zyte ? "unknown" : zyte.configured ? "connected" : "error"}
            actionSlot={isAdmin && (
              <button onClick={testZyte} disabled={testing.zyte || !zyte?.configured} data-testid="test-zyte-btn"
                className="text-xs border border-[rgba(255,255,255,0.15)] text-[#8EAADF] hover:text-white px-3 py-1.5 rounded-lg transition-colors disabled:opacity-50">
                {testing.zyte ? "Testing…" : "Test"}
              </button>
            )}>
            <StatRow label="API key"         value={zyte?.key_preview ?? "Not set"} />
            <StatRow label="Method"          value="AI-powered browser rendering" mono={false} />
            <StatRow label="Tier"            value="4b — Runs parallel with Bright Data" mono={false} />
            <StatRow label="Monthly fetches" value={fmt(zyte?.monthly_fetches)} />
            <StatRow label="Fares stored"    value={fmt(zyte?.fares_stored)} />
            <StatRow label="Last fetch"      value={fmtDate(zyte?.last_fetch)} mono={false} />
            <StatRow label="Airlines"        value="SQ, MU, FJ, KE, CX, MH, QF + all others" mono={false} />
            {testResult.zyte && (
              <div className={`mt-3 p-3 rounded-lg text-xs font-mono ${
                testResult.zyte.status === "connected" ? "bg-emerald-900/30 text-emerald-400"
                : testResult.zyte.status === "reachable" ? "bg-amber-900/30 text-amber-400"
                : "bg-red-900/30 text-red-400"}`}>
                {testResult.zyte.status === "connected"
                  ? `✓ Connected · ${testResult.zyte.latency_ms}ms · Live fare: NZD ${testResult.zyte.sample_fare?.toLocaleString()}`
                  : testResult.zyte.status === "reachable"
                  ? `⚠ Connected · ${testResult.zyte.latency_ms}ms · ${testResult.zyte.message}`
                  : `✗ ${testResult.zyte.message}`}
              </div>
            )}
            <div className="mt-3 p-3 bg-[rgba(255,255,255,0.03)] rounded-lg">
              <p className="text-xs text-[#8EAADF]">
                Runs in parallel with Bright Data for missing airlines. Zyte uses AI to render
                full JavaScript pages and extract prices automatically — highest success rate
                on protected airline sites including SQ, MU, FJ.
              </p>
            </div>
          </SourceCard>
        </div>
      </section>

      <section>
        <h2 className="font-syne font-bold text-white text-base mb-3">Scheduled Run History</h2>
        <div className="bg-[#0F2044] rounded-xl border border-[rgba(255,255,255,0.08)] overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[rgba(255,255,255,0.08)]">
                {["Job", "Status", "Success", "Failed", "Duration", "Completed"].map(h => (
                  <th key={h} className="text-left text-[#8EAADF] font-normal text-xs px-4 py-3">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {schedHistory.length === 0 ? (
                <tr><td colSpan={6} className="text-center text-[#8EAADF] text-xs py-6">No runs recorded yet</td></tr>
              ) : schedHistory.map(run => (
                <tr key={run.id} className="border-b border-[rgba(255,255,255,0.04)] last:border-0 hover:bg-[rgba(255,255,255,0.02)]">
                  <td className="px-4 py-2.5 text-white text-xs font-mono">{run.job_type}</td>
                  <td className="px-4 py-2.5">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-mono ${
                      run.status === "success" ? "bg-emerald-900/40 text-emerald-400"
                      : run.status === "running" ? "bg-blue-900/40 text-blue-400"
                      : run.status === "partial" ? "bg-amber-900/40 text-amber-400"
                      : "bg-red-900/40 text-red-400"}`}>
                      {run.status}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-emerald-400 text-xs font-mono">{run.successful_calls}</td>
                  <td className="px-4 py-2.5 text-red-400 text-xs font-mono">{run.failed_calls || 0}</td>
                  <td className="px-4 py-2.5 text-[#8EAADF] text-xs font-mono">{run.duration_seconds ? `${run.duration_seconds}s` : "—"}</td>
                  <td className="px-4 py-2.5 text-[#8EAADF] text-xs">{fmtDate(run.completed_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section>
        <h2 className="font-syne font-bold text-white text-base mb-3">Data Freshness — Google Flights (11 routes × 9 months)</h2>
        <div className="bg-[#0F2044] rounded-xl border border-[rgba(255,255,255,0.08)] p-5">
          <div className="flex gap-4 text-xs mb-4">
            <span className="flex items-center gap-1.5"><span className="inline-block w-2.5 h-2.5 rounded bg-emerald-400" /> &lt;26h</span>
            <span className="flex items-center gap-1.5"><span className="inline-block w-2.5 h-2.5 rounded bg-amber-400" /> 26–50h</span>
            <span className="flex items-center gap-1.5"><span className="inline-block w-2.5 h-2.5 rounded bg-red-400" /> &gt;50h</span>
            <span className="flex items-center gap-1.5"><span className="inline-block w-2.5 h-2.5 rounded bg-[rgba(255,255,255,0.08)]" /> Not fetched</span>
          </div>
          <FreshnessGrid />
        </div>
      </section>

      <section>
        <h2 className="font-syne font-bold text-white text-base mb-3">Recent Fetch Jobs</h2>
        <div className="bg-[#0F2044] rounded-xl border border-[rgba(255,255,255,0.08)] overflow-hidden">
          <table className="w-full text-sm" data-testid="fetch-jobs-table">
            <thead>
              <tr className="border-b border-[rgba(255,255,255,0.08)]">
                {["Route", "Cabin", "Month", "Source", "Status", "Airlines", "Duration", "Started"].map(h => (
                  <th key={h} className="text-left text-[#8EAADF] font-normal text-xs px-4 py-3">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {fetchJobs.length === 0 ? (
                <tr><td colSpan={8} className="text-center text-[#8EAADF] text-xs py-6">No fetch jobs yet</td></tr>
              ) : fetchJobs.map((job, i) => (
                <tr key={job.id || i} className="border-b border-[rgba(255,255,255,0.04)] last:border-0 hover:bg-[rgba(255,255,255,0.02)]">
                  <td className="px-4 py-2 text-white text-xs font-mono">{job.route_id?.slice(0, 8)}…</td>
                  <td className="px-4 py-2 text-[#8EAADF] text-xs">{job.cabin_class}</td>
                  <td className="px-4 py-2 text-[#8EAADF] text-xs font-mono">{job.travel_month}/{job.travel_year}</td>
                  <td className="px-4 py-2">
                    <span className={`text-xs px-1.5 py-0.5 rounded font-mono ${
                      job.source === "airline_direct" ? "bg-[#1B52C4]/30 text-blue-300"
                      : "bg-[rgba(255,255,255,0.06)] text-[#8EAADF]"}`}>
                      {job.source === "airline_direct" ? "CI" : "GF"}
                    </span>
                  </td>
                  <td className="px-4 py-2">
                    <span className={`text-xs px-1.5 py-0.5 rounded-full font-mono ${
                      job.status === "success" ? "bg-emerald-900/40 text-emerald-400"
                      : job.status === "running" ? "bg-blue-900/40 text-blue-400"
                      : job.status === "failed" ? "bg-red-900/40 text-red-400"
                      : "bg-gray-800 text-gray-400"}`}>
                      {job.status}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-[#8EAADF] text-xs font-mono">{job.airlines_returned ?? "—"}</td>
                  <td className="px-4 py-2 text-[#8EAADF] text-xs font-mono">{job.duration_ms ? `${job.duration_ms}ms` : "—"}</td>
                  <td className="px-4 py-2 text-[#8EAADF] text-xs">{fmtDate(job.started_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

    </div>
  );
}
